import React from 'react';

const QuestionSection = ({ answers, onAnswerChange, onSubmit, isValid, isAnalyzing }) => {
  const handleRadioChange = (questionId, value) => {
    onAnswerChange(questionId, value);
  };

  const handleCheckboxChange = (questionId, value) => {
    const currentValues = answers[questionId] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    onAnswerChange(questionId, newValues);
  };

  const isChecked = (questionId, value) => {
    return answers[questionId]?.includes(value) || false;
  };

  const isAnswered = (questionId) => {
    const answer = answers[questionId];
    if (Array.isArray(answer)) {
      return answer.length > 0;
    }
    return answer !== '';
  };

  return (
    <section className="px-4 py-6 bg-slate-50" id="quick-check">
      <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm relative">
        <div className="flex justify-between items-end mb-6 border-b border-slate-100 pb-4 gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <span className="material-symbols-outlined text-blue-btn-primary">edit_note</span>
              1分簡易診断
            </h2>
            <p className="text-xs text-slate-500 mt-1">当てはまるものを選んでください（複数選択可/全7問）</p>
          </div>
          <div className="text-right whitespace-nowrap">
            <p className="text-[10px] font-bold text-slate-500 mb-1">全7問</p>
          </div>
        </div>

        <form className="flex flex-col gap-8">
          <div className="group/q1">
            <label className="text-slate-900 text-sm font-bold mb-3 flex items-center">
              <span className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center mr-2 font-bold ${
                isAnswered('q1') ? 'bg-blue-btn-primary text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                Q1
              </span>
              性別
            </label>
            <div className="flex flex-wrap gap-2">
              {['男性', '女性', '未回答'].map((option) => (
                <label key={option} className="cursor-pointer">
                  <input 
                    className="peer sr-only" 
                    name="gender" 
                    type="radio"
                    checked={answers.q1 === option}
                    onChange={() => handleRadioChange('q1', option)}
                  />
                  <div className="px-4 py-2 rounded-lg border border-slate-100 bg-white text-slate-600 peer-checked:bg-blue-btn-primary peer-checked:border-blue-btn-primary peer-checked:text-white transition-all text-sm hover:bg-gray-50">
                    {option}
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="group/q2">
            <label className="text-slate-900 text-sm font-bold mb-3 flex items-center">
              <span className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center mr-2 font-bold ${
                isAnswered('q2') ? 'bg-blue-btn-primary text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                Q2
              </span>
              年代
            </label>
            <div className="flex flex-wrap gap-2">
              {['10代', '20代', '30代', '40代', '50代', '60代', '70代', '80代'].map((option) => (
                <label key={option} className="cursor-pointer">
                  <input 
                    className="peer sr-only" 
                    name="age" 
                    type="radio"
                    checked={answers.q2 === option}
                    onChange={() => handleRadioChange('q2', option)}
                  />
                  <div className="px-3 py-2 rounded-lg border border-slate-100 bg-white text-slate-600 peer-checked:bg-blue-btn-primary peer-checked:border-blue-btn-primary peer-checked:text-white transition-all text-sm hover:bg-gray-50">
                    {option}
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="group/q3">
            <label className="text-slate-900 text-sm font-bold mb-3 flex items-center">
              <span className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center mr-2 font-bold ${
                isAnswered('q3') ? 'bg-blue-btn-primary text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                Q3
              </span>
              いま気になることは？
            </label>
            <div className="flex flex-wrap gap-2">
              {['乾燥', 'かゆみ', 'ベタつき', 'におい', '抜け毛が気になる', '特になし'].map((option) => (
                <label key={option} className="cursor-pointer">
                  <input 
                    className="peer sr-only" 
                    name="q3[]" 
                    type="checkbox"
                    checked={isChecked('q3', option)}
                    onChange={() => handleCheckboxChange('q3', option)}
                  />
                  <div className="px-3 py-2 rounded-lg border border-slate-100 bg-white text-slate-600 peer-checked:bg-blue-btn-primary peer-checked:border-blue-btn-primary peer-checked:text-white transition-all text-sm hover:bg-gray-50 flex items-center gap-1">
                    <svg className="w-4 h-4 hidden peer-checked:block" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    {option}
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="group/q4">
            <label className="text-slate-900 text-sm font-bold mb-3 flex items-center">
              <span className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center mr-2 font-bold ${
                isAnswered('q4') ? 'bg-blue-btn-primary text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                Q4
              </span>
              髪の状態で近いものは？
            </label>
            <div className="flex flex-wrap gap-2">
              {['パサつき', '広がり', 'うねり', 'ハリ・コシがない', '特になし'].map((option) => (
                <label key={option} className="cursor-pointer">
                  <input 
                    className="peer sr-only" 
                    name="q4[]" 
                    type="checkbox"
                    checked={isChecked('q4', option)}
                    onChange={() => handleCheckboxChange('q4', option)}
                  />
                  <div className="px-3 py-2 rounded-lg border border-slate-100 bg-white text-slate-600 peer-checked:bg-blue-btn-primary peer-checked:border-blue-btn-primary peer-checked:text-white transition-all text-sm hover:bg-gray-50 flex items-center gap-1">
                    <svg className="w-4 h-4 hidden peer-checked:block" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    {option}
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="group/q5">
            <label className="text-slate-900 text-sm font-bold mb-3 flex items-center">
              <span className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center mr-2 font-bold ${
                isAnswered('q5') ? 'bg-blue-btn-primary text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                Q5
              </span>
              洗髪の頻度は？
            </label>
            <div className="flex flex-wrap gap-2">
              {['毎日', '1日おき', '週2〜3回', '不規則'].map((option) => (
                <label key={option} className="cursor-pointer">
                  <input 
                    className="peer sr-only" 
                    name="q5" 
                    type="radio"
                    checked={answers.q5 === option}
                    onChange={() => handleRadioChange('q5', option)}
                  />
                  <div className="px-3 py-2 rounded-lg border border-slate-100 bg-white text-slate-600 peer-checked:bg-blue-btn-primary peer-checked:border-blue-btn-primary peer-checked:text-white transition-all text-sm hover:bg-gray-50">
                    {option}
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="group/q6">
            <label className="text-slate-900 text-sm font-bold mb-3 flex items-center">
              <span className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center mr-2 font-bold ${
                isAnswered('q6') ? 'bg-blue-btn-primary text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                Q6
              </span>
              洗った後の頭皮の感覚は？
            </label>
            <div className="flex flex-wrap gap-2">
              {['つっぱる', 'すっきり', 'べたつく', 'よく分からない'].map((option) => (
                <label key={option} className="cursor-pointer">
                  <input
                    className="peer sr-only"
                    name="q6"
                    type="radio"
                    checked={answers.q6 === option}
                    onChange={() => handleRadioChange('q6', option)}
                  />
                  <div className="px-3 py-2 rounded-lg border border-slate-100 bg-white text-slate-600 peer-checked:bg-blue-btn-primary peer-checked:border-blue-btn-primary peer-checked:text-white transition-all text-sm hover:bg-gray-50">
                    {option}
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="group/q7">
            <label className="text-slate-900 text-sm font-bold mb-3 flex items-center">
              <span className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center mr-2 font-bold ${
                isAnswered('q7') ? 'bg-blue-btn-primary text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                Q7
              </span>
              最近の生活で近いものは？
            </label>
            <div className="flex flex-wrap gap-2">
              {['睡眠不足', 'ストレス多め', '食生活が乱れがち', '特に変化なし'].map((option) => (
                <label key={option} className="cursor-pointer">
                  <input 
                    className="peer sr-only" 
                    name="q7[]" 
                    type="checkbox"
                    checked={isChecked('q7', option)}
                    onChange={() => handleCheckboxChange('q7', option)}
                  />
                  <div className="px-3 py-2 rounded-lg border border-slate-100 bg-white text-slate-600 peer-checked:bg-blue-btn-primary peer-checked:border-blue-btn-primary peer-checked:text-white transition-all text-sm hover:bg-gray-50 flex items-center gap-1">
                    <svg className="w-4 h-4 hidden peer-checked:block" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    {option}
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-dashed border-gray-200">
            <button 
              className="w-full h-12 bg-slate-900 text-white rounded-xl font-bold shadow-lg hover:bg-black transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" 
              type="button"
              onClick={onSubmit}
              disabled={!isValid || isAnalyzing}
            >
              <span className="material-symbols-outlined text-accent">smart_toy</span>
              {isAnalyzing ? '分析中...' : 'ケアリスト AI Niita に相談する'}
            </button>
            <p className="text-[9px] text-center text-slate-500 mt-3 leading-relaxed">
              Niita (Needs Identification & Individualized Treatment AI) は頭皮laboオリジナルのAIシステムです。ニーズ特定と個別ケア提案を高度に行います。
            </p>
          </div>
        </form>
      </div>
    </section>
  );
};

export default QuestionSection;
